import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAppearance } from "../../contexts/AppearanceContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Trophy, XCircle, MoreHorizontal, ArrowDown } from "lucide-react";
import * as XLSX from "xlsx";
import { Button } from "../../components/ui/button";
import { Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtK(v) {
  if (!v) return "R$ 0";
  if (v >= 1000000) return `R$ ${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `R$ ${(v / 1000).toFixed(0)}k`;
  return `R$ ${Math.round(v)}`;
}

export default function ConversaoReport({filterHook }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState("");
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3b82f6";

  useEffect(() => {
    // Sync with FilterBar pipeline selection
    if (filterHook.filters.pipelineIds.length > 0) {
      setSelectedPipeline(filterHook.filters.pipelineIds[0]);
    } else if (filterHook.pipelines.length && !selectedPipeline) {
      setSelectedPipeline(filterHook.pipelines[0]?.id || "");
    }
  }, [filterHook.filters.pipelineIds, filterHook.pipelines]); // eslint-disable-line

  const fetchData = useCallback(async () => {
    if (!selectedPipeline) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/conversao-etapa/${selectedPipeline}`, { withCredentials: true });
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [selectedPipeline]);

  useEffect(() => { if (selectedPipeline) fetchData(); }, [fetchData, selectedPipeline]);

  const exportExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data.stages.map(s => ({
      "Etapa": s.name, "Negócios": s.count, "MRR": s.mrr,
      "Conversão para próxima %": s.conversion_to_next ?? "—",
    })));
    XLSX.utils.book_append_sheet(wb, ws, "Conversão por Etapa");
    XLSX.writeFile(wb, `conversao_etapa_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Select value={selectedPipeline} onValueChange={setSelectedPipeline}>
            <SelectTrigger className="w-48 h-9 text-sm"><SelectValue placeholder="Selecionar pipeline..." /></SelectTrigger>
            <SelectContent>{filterHook.pipelines.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-xs text-slate-400">Taxa de conversão entre etapas do funil</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportExcel} className="gap-2 text-slate-600">
          <Download className="w-4 h-4" />{t("relatorios.exportExcel")}</Button>
      </div>

      {loading && <div className="flex items-center justify-center h-32 text-slate-400 text-sm">{t("ui.calculando_conversoes")}</div>}

      {!loading && data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Funil de conversão */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
            <h3 className="text-lg font-bold text-slate-900 text-center mb-6">{t("ui.taxa_conversao_etapa")}</h3>
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-[19px] top-4 bottom-4 w-0.5 bg-slate-200" />
              <div className="space-y-0">
                {(data.stages || []).map((stage, i) => {
                  const isFinal = stage.is_final;
                  const convRate = stage.conversion_to_next;
                  const isLowConv = convRate !== null && convRate !== undefined && convRate < 50;
                  return (
                    <div key={stage.stage_id}>
                      {/* Stage row */}
                      <div className="flex items-center gap-4 relative">
                        {/* Dot */}
                        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 z-10 border-2 border-white shadow-sm"
                          style={{ backgroundColor: stage.color }}>
                          <span className="text-white text-xs font-bold">{stage.count}</span>
                        </div>
                        {/* Info */}
                        <div className="flex-1 py-3">
                          <p className="text-sm font-bold text-slate-900">{stage.name}</p>
                          <p className="text-xs text-slate-400">{stage.count} negócio{stage.count !== 1 ? "s" : ""}{stage.mrr > 0 ? ` · ${fmtK(stage.mrr)}` : ""}</p>
                        </div>
                        {/* MRR bar */}
                        {!isFinal && stage.count > 0 && (
                          <div className="w-32 hidden sm:block">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-slate-100 rounded-full h-1.5">
                                <div className="h-1.5 rounded-full" style={{ width: `${Math.min(100, stage.count * 2)}%`, backgroundColor: stage.color }} />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Conversion arrow between stages */}
                      {convRate !== null && convRate !== undefined && (
                        <div className="flex items-center gap-2 ml-3 py-1">
                          <div className="flex flex-col items-center w-4">
                            <ArrowDown className="w-3 h-3 text-slate-300" />
                          </div>
                          <span className={`text-sm font-bold ${isLowConv ? "text-rose-500" : "text-slate-500"}`}>
                            {convRate}%
                          </span>
                          {isLowConv && <span className="text-[10px] text-rose-400 bg-rose-50 px-1.5 py-0.5 rounded">baixa</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sumário Mensal */}
          <div className="space-y-3">
            <div className="bg-slate-900 rounded-2xl p-5 text-white">
              <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider mb-5 text-center">{t("ui.sumario_mensal")}</h3>
              <div className="space-y-5">
                {/* Ganhos */}
                <div className="flex flex-col items-center gap-2">
                  <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-bold text-white">{t("negocios.won")}</p>
                    <p className="text-xl font-bold text-emerald-400">{fmtK(data.summary.mrr_ganhos_mes)}</p>
                    <p className="text-xs text-slate-400">{data.summary.ganhos_mes} negócio{data.summary.ganhos_mes !== 1 ? "s" : ""}</p>
                  </div>
                </div>
                <div className="border-t border-slate-700" />
                {/* Andamento */}
                <div className="flex flex-col items-center gap-2">
                  <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <MoreHorizontal className="w-6 h-6 text-amber-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-bold text-white">{t("ui.andamento")}</p>
                    <p className="text-xl font-bold text-amber-400">{fmtK(data.summary.mrr_em_andamento)}</p>
                    <p className="text-xs text-slate-400">{data.summary.em_andamento} negócio{data.summary.em_andamento !== 1 ? "s" : ""}</p>
                  </div>
                </div>
                <div className="border-t border-slate-700" />
                {/* Perdidos */}
                <div className="flex flex-col items-center gap-2">
                  <div className="w-14 h-14 rounded-full bg-rose-500/20 flex items-center justify-center">
                    <XCircle className="w-6 h-6 text-rose-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-bold text-white">{t("negocios.lost")}</p>
                    <p className="text-xl font-bold text-rose-400">{fmtK(data.summary.mrr_perdidos_mes)}</p>
                    <p className="text-xs text-slate-400">{data.summary.perdidos_mes} negócio{data.summary.perdidos_mes !== 1 ? "s" : ""}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
